/**
 * O `Destino` do contrato, do lado da TELA — [spec-008 T030c].
 *
 * `contracts/transporte.md` define nove eventos que o transporte emite para
 * cima. Até a [T030b] **nenhum `.vue` do repositório respondia a oito deles**
 * (busca no `src/` inteiro): com a bandeira ligada a UI de voz aparecia e o
 * microfone abria, mas o áudio de volta não era anexado a elemento nenhum e
 * `estadoDaFala` não acompanhava a IA. Sessão PAGA e MUDA.
 *
 * Este módulo é a política desses oito eventos; o `.vue` só provê os quatro
 * GANCHOS que exigem DOM ou navegação (anexar o fluxo a um `<audio>`, dizer
 * uma linha, sair da voz, cair para o v1). A divisão é a mesma do resto do
 * módulo — `transporte-tempo-real-eventos.js` traduz o fio sem tocar DOM;
 * aqui se decide o que a tela mostra, sem tocar DOM também.
 *
 * ## ⚠️ `estadoDaFala` arma o v1 pela porta dos fundos — e a guarda NÃO mora aqui
 *
 * `transporte-por-turnos.js` instala `$watch` em `motorDeVoz.estadoDaFala` e
 * ele fica armado desde a carga do módulo: escrever `'ia'` liga a escuta de
 * barge-in LOCAL e a marca POSICIONAL do v1, **sem uma linha importando
 * `barge-in-de-voz.js`** (medido por mutação na [T030]). Foi por isso que o
 * transporte em tempo real nasceu sem escrever esse campo.
 *
 * A guarda que torna seguro escrevê-lo AQUI é a da [T030b]:
 * `if (!porTurnosNoComando()) return`, dentro dos dois `$watch`. Ela é de lá
 * e **de propósito não é repetida neste arquivo**: uma segunda guarda com o
 * mesmo predicado faria o teste "o v1 não é armado" passar por causa DELA —
 * e apagar a guarda de `transporte-por-turnos.js` deixaria a suíte verde,
 * que é a definição de teste vacuoso. O teste desta task exercita a guarda
 * real, com o caso de CONTROLE provando que o mesmo `'ia'` com o `porTurnos`
 * no comando ARMA o barge-in.
 *
 * ## A ORDEM no encerramento é invariante, não estilo
 *
 * `estadoDaFala` cai ANTES de o comando voltar ao `porTurnos`. Devolver o
 * comando primeiro (é o que `sairDoModoDeVoz` faz na fachada) rearma os dois
 * `$watch`; a descida `'ia' → null` que vem depois entra no `else` do watcher
 * e chama `marcarFimDeFalaAtual()` — uma linha de medição do v1 sobre uma
 * fala que ele nunca tocou (N10: medição com fato errado é pior que medição
 * faltando). Mesmo racional do `_devolverComandoAoPorTurnos` da fachada, que
 * derruba `sessaoDeVozAtiva` antes de soltar o comando.
 */
import { motorDeVoz } from './estado-da-voz'
import {
  guardarFluxoRemoto,
  limparVozTempoReal,
  vozTempoReal,
} from './estado-da-voz-tempo-real'

const _chamar = (gancho, argumento) => {
  if (typeof gancho !== 'function') return
  gancho(argumento)
}

/**
 * ★ [T050] A UMA linha quando a consulta do turno falado NÃO saiu.
 *
 * Diz as duas coisas que o consultor precisa: o que aconteceu (ninguém foi
 * buscar) e o que fazer (repetir). Sem a segunda metade, "não consegui
 * consultar" deixa a pessoa esperando de novo — que é o estado em que ela já
 * estava. Sem motivo técnico (N7): ele vai para a medição.
 */
export const MENSAGEM_TURNO_NAO_CONSULTADO =
  'Não consegui consultar isso agora. Pode repetir a pergunta?'

/**
 * Os oito eventos do `Destino` que a sessão em TEMPO REAL emite.
 *
 * O objeto devolvido é feito para ser ESPALHADO sobre o destino do v1
 * (`aoPreencherTexto`/`aoEnviarVoz`/`avisarErroDeTranscricao`/`focarCampo`,
 * em `send-message.vue`): as duas metades são disjuntas em nome, e o
 * transporte por turnos ignora o que não conhece — ele só chama o que
 * existe. Um destino por transporte obrigaria o componente a saber qual está
 * no comando, que é justamente o que o contrato proíbe ("`nome` NUNCA é lido
 * por componente de tela").
 *
 * @param {Object} ganchos
 * @param {(stream: MediaStream) => void} ganchos.anexarAudio o `<audio>` da
 *   tela. `audioRemoto` entrega um **`MediaStream`**, não data channel — o
 *   transporte fica sem DOM por desenho.
 * @param {(mensagem: string) => void} ganchos.avisar a UMA linha da tela.
 *   Nunca carrega motivo técnico (N7).
 * @param {() => void} ganchos.encerrarVoz sai da UI de voz — a sessão acabou
 *   por fora da fachada (teto, foco, pedido do provedor).
 * @param {() => void} ganchos.cairParaPorTurnos o canal caiu no MEIO da
 *   sessão: encerra o tempo real e segue pelo Modo de Voz de sempre. É o que
 *   torna a mensagem do [T034] verdadeira — ela promete "seguimos pelo modo
 *   de voz de sempre", e promessa sem o segundo passo é aviso mentiroso.
 * @param {string} ganchos.mensagemDeQueda a copy da queda ([T034]).
 */
export const criarDestinoDeVozTempoReal = ({
  anexarAudio,
  avisar,
  encerrarVoz,
  cairParaPorTurnos,
  mensagemDeQueda,
} = {}) => {
  /**
   * O fim da sessão, por qualquer motivo. Ver "A ORDEM no encerramento" no
   * cabeçalho: `estadoDaFala` primeiro, comando depois.
   */
  const _limparTela = () => {
    motorDeVoz.estadoDaFala = null
    limparVozTempoReal()
  }

  return {
    /**
     * A trilha de áudio do provedor. Quem a anexa é a TELA — é este o evento
     * sem o qual a sessão fica muda, e é o único do contrato cujo payload é
     * um objeto do navegador em vez de dado.
     */
    audioRemoto: (payload) => {
      const fluxo = payload && payload.stream
      if (!fluxo) return
      // Guardado ANTES de anexar: a instância que está na tela agora pode ser
      // destruída no gesto seguinte (herói → rodapé), e é deste fluxo que a
      // próxima se serve. Ver `estado-da-voz-tempo-real.js`.
      guardarFluxoRemoto(fluxo)
      _chamar(anexarAudio, fluxo)
    },

    /** O consultor está falando agora. */
    transcricaoParcial: (payload) => {
      vozTempoReal.transcricaoParcial = String((payload && payload.texto) || '')
    },

    /**
     * O turno do consultor fechou. A parcial some no MESMO instante: manter
     * as duas na tela mostraria a mesma frase duas vezes, e a parcial é
     * sempre a versão pior (o juiz do provedor ainda não tinha decidido).
     */
    transcricaoFinal: (payload) => {
      vozTempoReal.transcricaoDoTurno = String((payload && payload.texto) || '')
      vozTempoReal.transcricaoParcial = ''
    },

    /**
     * A IA começou a falar. É ESTA linha que faz a orb do composer pintar de
     * `--primary` numa sessão em tempo real — `estadoVisualDoOrb` lê
     * `estadoDaFala`, e sem ela a UI ficava no estado "aguardando" enquanto a
     * IA falava.
     */
    falaDaIaComecou: () => {
      motorDeVoz.estadoDaFala = 'ia'
    },

    falaDaIaTerminou: () => {
      motorDeVoz.estadoDaFala = null
    },

    /**
     * A escrita pedida por voz deixou uma decisão esperando NA TELA (US-5).
     *
     * ⚠️ Só o `idProposta` chega — **e é assim que D-13/N16 se cumpre**: o
     * dado de negócio (valor, cliente, ativo, data-base) a tela lê de onde
     * ele já vive, nunca do canal falado, que altera identificador em 6/11
     * das medições (G-11). Encaminhar sem valor é o ponto.
     */
    propostaAguardando: (payload) => {
      const id = payload && payload.idProposta
      if (!id) return
      vozTempoReal.propostaAguardando = { idProposta: String(id) }
    },

    /**
     * Falta pouco para o teto da sessão paga, ou o foco se perdeu ([T035]).
     *
     * `cancelado: true` (voltar ao foco) APAGA o aviso: deixar "vou encerrar"
     * na tela depois de o motivo passar é afirmação falsa.
     */
    avisoDeEncerramento: (payload) => {
      const aviso = payload || {}
      if (aviso.cancelado) {
        vozTempoReal.avisoDeEncerramento = null
        return
      }
      vozTempoReal.avisoDeEncerramento = {
        motivo: String(aviso.motivo || ''),
        segundosRestantes: Number(aviso.segundosRestantes || 0),
      }
    },

    /**
     * A sessão acabou POR FORA da fachada (teto, foco, pedido do provedor).
     * Sem isto a tela seguiria desenhando voz sobre um canal morto — e o
     * comando continuaria com um transporte fechado, calando os dois `$watch`
     * do v1 para a sessão SEGUINTE.
     */
    encerrada: () => {
      _limparTela()
      _chamar(encerrarVoz)
    },

    /**
     * O transporte não conseguiu seguir. Diz a UMA linha do [T034] e segue
     * pelo Modo de Voz de sempre — o motivo técnico fica na medição, que é
     * quem o registrou (`_aoMudarConexao` já chamou `marcarFalhaDaMedicao`).
     */
    queda: () => {
      _limparTela()
      if (mensagemDeQueda) _chamar(avisar, mensagemDeQueda)
      _chamar(cairParaPorTurnos)
    },

    /**
     * ★ [T050] A CONSULTA NÃO SAIU — e até aqui ninguém dizia isso.
     *
     * `erro` é o nono evento do `Destino` e **não tinha morador**: as quatro
     * portas de recusa de `transporte-tempo-real-delegacao.js` emitem `erro`,
     * e `emitir` é `if (typeof _destino[evento] !== 'function') return`. Com o
     * evento sem manipulador, TODA recusa morria calada — o consultor falava,
     * via a própria transcrição e esperava por uma resposta que ninguém tinha
     * ido buscar.
     *
     * Por que ninguém pegou isso em 735 unitárias: o dublê de destino do irmão
     * (`transporte-tempo-real.unit.js::criarDestinoFalso`) declara
     * `erro: jest.fn()` — um destino que a tela não tem. O teste media o
     * dublê, não o morador.
     *
     * A sessão **não** cai por aqui: `erro` é "este turno não deu", não "o
     * canal morreu" (esse é `queda`). O consultor pode repetir a pergunta.
     *
     * ⚠️ N7 — o `motivo` técnico (`delegacao_em_curso`,
     * `identificador_nao_confere`, …) vai para a MEDIÇÃO, nunca para a tela:
     * quem registra é o próprio ponto de recusa, com `marcarFalhaDaMedicao`.
     * Aqui sai só a linha que o consultor consegue agir sobre.
     */
    erro: () => {
      _chamar(avisar, MENSAGEM_TURNO_NAO_CONSULTADO)
    },
  }
}
